import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import {
  DEFAULT_PDF_DATEINAME,
  GD_ANSPRECHPARTNER_OPTIONS,
  GD_BETREFF_FONT_SIZE_OPTIONS,
  GD_BETREFFZEILE1_QUELLE_OPTIONS,
  GD_FONT_OPTIONS,
  GD_LEISTUNGSDATUM_OPTIONS,
  GD_POSITIONSTEXT_FONT_SIZE_OPTIONS,
  GD_POSITIONSTEXT_TYP_OPTIONS,
  GD_ZAHLUNGSART_OPTIONS,
  GD_ZAHLUNGSBEDINGUNG_ART_OPTIONS,
  type GdBetreffzeile,
  type GdInfoblock,
  type GdLayoutConfig,
  type GdLogoFirma,
  type GdPositionstext,
  type GdPositionstextStyle,
  type GdPositionstextTypKey,
  type GdQrSignatur,
  type GdZusaetzlicheOptionen,
} from "@/lib/gdLayoutConfig";
import {
  fetchGdPlaceholderCatalog,
  type GdPlaceholderCategory,
} from "@/lib/gdPlaceholders";
import { GdPlaceholderPicker } from "@/pages/dokumente/GdPlaceholderPicker";

function num(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function fontLabel(font: string): string {
  return font.split(",")[0]?.trim() ?? font;
}

function BetreffzeileFields({
  title,
  subtitle,
  config,
  onChange,
  showQuelle,
  showPraefix,
  showFormat,
}: {
  title: string;
  subtitle?: string;
  config: GdBetreffzeile;
  onChange: (next: GdBetreffzeile) => void;
  showQuelle?: boolean;
  showPraefix?: boolean;
  showFormat?: boolean;
}) {
  function patch(partial: Partial<GdBetreffzeile>) {
    onChange({ ...config, ...partial });
  }

  return (
    <div className="gd-layout-config__field-group">
      <h3>{title}</h3>
      {subtitle ? <p className="gd-layout-config__hint">{subtitle}</p> : null}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
        <Checkbox
          label="Betreff anzeigen"
          checked={config.anzeigen}
          onChange={(e) => patch({ anzeigen: e.target.checked })}
        />
        <Checkbox
          label="fettgedruckt"
          checked={config.fett}
          onChange={(e) => patch({ fett: e.target.checked })}
        />
      </div>
      {showQuelle ? (
        <SelectInput
          label="Was soll in der ersten Betreffzeile angezeigt werden?"
          value={config.quelle ?? "projektname"}
          onChange={(e) => patch({ quelle: e.target.value })}
        >
          {GD_BETREFFZEILE1_QUELLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SelectInput>
      ) : null}
      {showPraefix ? (
        <TextInput
          label="Präfix der Betreffzeile"
          value={config.praefix ?? ""}
          onChange={(e) => patch({ praefix: e.target.value })}
        />
      ) : null}
      {showFormat ? (
        <TextInput
          label="Format der Betreffzeile"
          value={config.format ?? "{typ}-Nr. {nummer}"}
          onChange={(e) => patch({ format: e.target.value })}
          hint="Platzhalter: {typ}, {nummer}"
        />
      ) : null}
      <div className="gd-layout-config__grid-2 mt-3">
        <SelectInput
          label="Schriftart"
          value={config.schriftart}
          onChange={(e) => patch({ schriftart: e.target.value })}
        >
          {GD_FONT_OPTIONS.map((font) => (
            <option key={font} value={font}>
              {fontLabel(font)}
            </option>
          ))}
        </SelectInput>
        <SelectInput
          label="Schriftgröße"
          value={String(config.schriftgroesse_pt)}
          onChange={(e) => patch({ schriftgroesse_pt: num(e.target.value) || 10 })}
        >
          {GD_BETREFF_FONT_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} pt
            </option>
          ))}
        </SelectInput>
      </div>
    </div>
  );
}

function PositionstextStyleFields({
  title,
  hint,
  config,
  onChange,
}: {
  title: string;
  hint?: string;
  config: GdPositionstextStyle;
  onChange: (next: GdPositionstextStyle) => void;
}) {
  function patch(partial: Partial<GdPositionstextStyle>) {
    onChange({ ...config, ...partial });
  }

  return (
    <div className="mt-3 rounded-md border border-[var(--eh-separator, #d1d1d6)] p-3">
      <h4 className="text-sm font-semibold text-label mb-1">{title}</h4>
      {hint ? <p className="gd-layout-config__hint mb-2">{hint}</p> : null}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
        <Checkbox
          label="fettgedruckt"
          checked={config.fett}
          onChange={(e) => patch({ fett: e.target.checked })}
        />
        <Checkbox
          label="kursiv"
          checked={config.kursiv}
          onChange={(e) => patch({ kursiv: e.target.checked })}
        />
        <Checkbox
          label="unterstrichen"
          checked={config.unterstrichen}
          onChange={(e) => patch({ unterstrichen: e.target.checked })}
        />
      </div>
      <div className="gd-layout-config__grid-2">
        <SelectInput
          label="Schriftart"
          value={config.schriftart}
          onChange={(e) => patch({ schriftart: e.target.value })}
        >
          {GD_FONT_OPTIONS.map((font) => (
            <option key={font} value={font}>
              {fontLabel(font)}
            </option>
          ))}
        </SelectInput>
        <SelectInput
          label="Schriftgröße"
          value={String(config.schriftgroesse_pt)}
          onChange={(e) => patch({ schriftgroesse_pt: num(e.target.value) || 9 })}
        >
          {(GD_POSITIONSTEXT_FONT_SIZE_OPTIONS.includes(config.schriftgroesse_pt)
            ? GD_POSITIONSTEXT_FONT_SIZE_OPTIONS
            : [...GD_POSITIONSTEXT_FONT_SIZE_OPTIONS, config.schriftgroesse_pt].sort((a, b) => a - b)
          ).map((size) => (
            <option key={size} value={size}>
              {size} pt
            </option>
          ))}
        </SelectInput>
        <div className="space-y-1 text-sm">
          <span className="text-label-secondary">Schriftfarbe</span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(config.schriftfarbe) ? config.schriftfarbe : "#636366"}
              onChange={(e) => patch({ schriftfarbe: e.target.value })}
              className="eh-input h-9 w-12 cursor-pointer p-1"
              aria-label={`${title} Schriftfarbe wählen`}
            />
            <input
              type="text"
              value={config.schriftfarbe}
              onChange={(e) => patch({ schriftfarbe: e.target.value })}
              className="eh-input w-full"
              placeholder="#636366"
              aria-label={`${title} Schriftfarbe Hex`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function PositionstextFields({
  config,
  onChange,
}: {
  config: GdPositionstext;
  onChange: (next: GdPositionstext) => void;
}) {
  const [activeTyp, setActiveTyp] = useState<GdPositionstextTypKey>("artikel");
  const typConfig = config[activeTyp];

  function patchTyp(partial: Partial<typeof typConfig>) {
    onChange({
      ...config,
      [activeTyp]: { ...typConfig, ...partial },
    });
  }

  return (
    <div className="gd-layout-config__field-group">
      <h3>Positionstexte</h3>
      <p className="gd-layout-config__hint">
        Formatierung je Positionsart. Bezeichnung = kurzer Titel der Zeile, Langtext = Beschreibung.
        Fett/Kursiv/Unterstrichen aus dem Positionstext-Editor bleiben zusätzlich wirksam.
      </p>
      <div className="flex flex-wrap gap-2 mt-3 mb-1">
        {GD_POSITIONSTEXT_TYP_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            type="button"
            size="sm"
            variant={activeTyp === opt.value ? "secondary" : "ghost"}
            onClick={() => setActiveTyp(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>
      {activeTyp === "titel" ? (
        <PositionstextStyleFields
          title="Bezeichnung"
          hint="Titelzeile in der Positionstabelle."
          config={typConfig.bezeichnung}
          onChange={(bezeichnung) => patchTyp({ bezeichnung })}
        />
      ) : null}
      {activeTyp === "text" ? (
        <>
          <PositionstextStyleFields
            title="Bezeichnung"
            hint="Optionaler Kurztitel einer Textposition."
            config={typConfig.bezeichnung}
            onChange={(bezeichnung) => patchTyp({ bezeichnung })}
          />
          <PositionstextStyleFields
            title="Langtext"
            hint="Hauptinhalt der Textposition."
            config={typConfig.langtext}
            onChange={(langtext) => patchTyp({ langtext })}
          />
        </>
      ) : null}
      {activeTyp === "artikel" || activeTyp === "leistung" ? (
        <>
          <PositionstextStyleFields
            title="Bezeichnung"
            hint="Kurzbezeichnung der Position."
            config={typConfig.bezeichnung}
            onChange={(bezeichnung) => patchTyp({ bezeichnung })}
          />
          <PositionstextStyleFields
            title="Langtext"
            hint="Beschreibung unter der Bezeichnung."
            config={typConfig.langtext}
            onChange={(langtext) => patchTyp({ langtext })}
          />
        </>
      ) : null}
    </div>
  );
}

function LogoFirmaFields({
  config,
  onChange,
}: {
  config: GdLogoFirma;
  onChange: (next: GdLogoFirma) => void;
}) {
  function patch(partial: Partial<GdLogoFirma>) {
    onChange({ ...config, ...partial });
  }

  return (
    <div className="gd-layout-config__field-group">
      <h3>Logo und Firmenanschrift</h3>
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
        <Checkbox
          label="Logo"
          checked={config.logo_anzeigen}
          onChange={(e) => patch({ logo_anzeigen: e.target.checked })}
        />
        <Checkbox
          label="Anschrift"
          checked={config.anschrift_anzeigen}
          onChange={(e) => patch({ anschrift_anzeigen: e.target.checked })}
        />
      </div>
      <div className="gd-layout-config__grid-2">
        <TextInput
          label="Position oben (mm)"
          type="number"
          value={String(config.position_oben_mm)}
          onChange={(e) => patch({ position_oben_mm: num(e.target.value) })}
        />
        <TextInput
          label="Position links (mm)"
          type="number"
          value={String(config.position_links_mm)}
          onChange={(e) => patch({ position_links_mm: num(e.target.value) })}
        />
      </div>
      {config.anschrift_anzeigen ? (
        <div className="mt-3">
          <TextareaInput
            label="Firmenanschrift (Zeilen)"
            value={config.firmenzeilen}
            onChange={(e) => patch({ firmenzeilen: e.target.value })}
            rows={4}
          />
        </div>
      ) : null}
    </div>
  );
}

function InfoblockFields({
  config,
  marginTopMm,
  onChange,
}: {
  config: GdInfoblock;
  marginTopMm: number;
  onChange: (next: GdInfoblock) => void;
}) {
  function patch(partial: Partial<GdInfoblock>) {
    onChange({ ...config, ...partial });
  }

  const positionError =
    config.anzeigen && config.position_oben_mm <= marginTopMm
      ? `Der Wert muss größer sein als der obere Seitenrand (${marginTopMm} mm).`
      : null;

  return (
    <div className="gd-layout-config__field-group">
      <h3>Dokumenteninformationen und Kontaktdaten</h3>
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
        <Checkbox
          label="Infoblock anzeigen"
          checked={config.anzeigen}
          onChange={(e) => patch({ anzeigen: e.target.checked })}
        />
        <Checkbox
          label="Bild Ansprechpartner/in"
          checked={config.ansprechpartner_bild}
          onChange={(e) => patch({ ansprechpartner_bild: e.target.checked })}
        />
      </div>
      <div className="gd-layout-config__grid-4">
        <TextInput
          label="Oben (mm)"
          type="number"
          value={String(config.position_oben_mm)}
          onChange={(e) => patch({ position_oben_mm: num(e.target.value) })}
          error={positionError ?? undefined}
        />
        <TextInput
          label="Links (mm)"
          type="number"
          value={String(config.position_links_mm)}
          onChange={(e) => patch({ position_links_mm: num(e.target.value) })}
        />
        <TextInput
          label="Breite (mm)"
          type="number"
          value={String(config.breite_mm)}
          onChange={(e) => patch({ breite_mm: num(e.target.value) })}
        />
      </div>

      <div className="gd-layout-config__field-group mt-4">
        <h3>Felder im Infoblock</h3>
        <div className="gd-layout-config__checkbox-grid">
          <Checkbox
            label="Projektnummer"
            checked={config.feld_projektnummer}
            onChange={(e) => patch({ feld_projektnummer: e.target.checked })}
          />
          <Checkbox
            label="Referenzdokument"
            checked={config.feld_referenzdokument}
            onChange={(e) => patch({ feld_referenzdokument: e.target.checked })}
          />
          <Checkbox
            label="Kundennummer"
            checked={config.feld_kundennummer}
            onChange={(e) => patch({ feld_kundennummer: e.target.checked })}
          />
          <Checkbox
            label="Bestellnummer"
            checked={config.feld_bestellnummer}
            onChange={(e) => patch({ feld_bestellnummer: e.target.checked })}
          />
          <Checkbox
            label="Kundenreferenz"
            checked={config.feld_kundenreferenz}
            onChange={(e) => patch({ feld_kundenreferenz: e.target.checked })}
          />
          <Checkbox
            label="USt-IdNr. des Empfängers"
            checked={config.feld_ust_id}
            onChange={(e) => patch({ feld_ust_id: e.target.checked })}
          />
          <Checkbox
            label="Dokumentnummer"
            checked={config.feld_dokumentnummer}
            onChange={(e) => patch({ feld_dokumentnummer: e.target.checked })}
          />
          <Checkbox
            label="Ihr Ansprechpartner (Kunde)"
            checked={config.feld_projektansprechpartner}
            onChange={(e) => patch({ feld_projektansprechpartner: e.target.checked })}
          />
        </div>
        <p className="mt-2 text-xs text-label-tertiary">
          Die Bestellnummer erscheint immer im Infoblock, sobald sie am Projekt hinterlegt ist.
        </p>
      </div>

      <div className="gd-layout-config__grid-2 mt-3">
        <SelectInput
          label="Liefer- oder Leistungsdatum anzeigen"
          value={config.leistungsdatum_modus}
          onChange={(e) => patch({ leistungsdatum_modus: e.target.value })}
        >
          {GD_LEISTUNGSDATUM_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SelectInput>
        <SelectInput
          label="Zahlungsart"
          value={config.zahlungsart_modus}
          onChange={(e) => patch({ zahlungsart_modus: e.target.value })}
        >
          {GD_ZAHLUNGSART_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SelectInput>
      </div>
      <p className="mt-2 text-xs text-label-tertiary">
        Weicht die Leistung vom Rechnungsdatum ab, erscheint der Zeitraum oder das Lieferdatum.
        Sonst der Vermerk „Leistungsdatum entspricht Rechnungsdatum“. Die E-Rechnung übernimmt das
        Datum in jedem Fall; maßgeblich für die Umsatzsteuer ist das Leistungsende.
      </p>

      <SelectInput
        label="Wer soll als Ansprechpartner/in aufgeführt werden?"
        value={config.ansprechpartner_modus}
        onChange={(e) => patch({ ansprechpartner_modus: e.target.value })}
        className="mt-3"
      >
        {GD_ANSPRECHPARTNER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </SelectInput>

      <div className="gd-layout-config__field-group mt-4">
        <h3>Kontaktdaten</h3>
        <div className="gd-layout-config__checkbox-grid">
          <Checkbox
            label="Name"
            checked={config.kontakt_name}
            onChange={(e) => patch({ kontakt_name: e.target.checked })}
          />
          <Checkbox
            label="Telefon"
            checked={config.kontakt_telefon}
            onChange={(e) => patch({ kontakt_telefon: e.target.checked })}
          />
          <Checkbox
            label="Mobil"
            checked={config.kontakt_mobil}
            onChange={(e) => patch({ kontakt_mobil: e.target.checked })}
          />
          <Checkbox
            label="Fax"
            checked={config.kontakt_fax}
            onChange={(e) => patch({ kontakt_fax: e.target.checked })}
          />
          <Checkbox
            label="E-Mail"
            checked={config.kontakt_email}
            onChange={(e) => patch({ kontakt_email: e.target.checked })}
          />
        </div>
      </div>
    </div>
  );
}

export function GdErsteSeiteContentPanel({
  layout,
  onChange,
}: {
  layout: GdLayoutConfig;
  onChange: (next: GdLayoutConfig) => void;
}) {
  function patch(partial: Partial<GdLayoutConfig>) {
    onChange({ ...layout, ...partial });
  }

  return (
    <>
      <div className="gd-layout-config__field-group">
        <h3>Abstand Adresse → Betreffzeile 1</h3>
        <p className="gd-layout-config__hint">
          Steuert den vertikalen Abstand zwischen Empfängeradresse und der ersten Betreffzeile.
        </p>
        <TextInput
          label="Abstand (mm)"
          type="number"
          value={String(layout.adresse_betreff_abstand_mm)}
          onChange={(e) => patch({ adresse_betreff_abstand_mm: num(e.target.value) })}
        />
      </div>
      <BetreffzeileFields
        title="Betreffzeile 1 (Projektanschrift)"
        subtitle="Erste Betreffzeile unter der Adresse."
        config={layout.betreffzeile1}
        onChange={(betreffzeile1) => patch({ betreffzeile1 })}
        showQuelle
        showPraefix
      />
      {layout.dokument_typ === "rechnung" ? (
        <div className="gd-layout-config__field-group">
          <h3>Abstand Leistungsempfänger → Rechnungsnummer</h3>
          <p className="gd-layout-config__hint">
            Steuert den vertikalen Abstand zwischen der Zeile Leistungsempfänger und der
            Rechnungsnummer. Die Zeile erscheint, wenn die Objektadresse vom Rechnungsempfänger
            abweicht.
          </p>
          <TextInput
            label="Abstand (mm)"
            type="number"
            value={String(layout.leistungempfaenger_abstand_mm)}
            onChange={(e) => patch({ leistungempfaenger_abstand_mm: num(e.target.value) })}
          />
        </div>
      ) : null}
      <BetreffzeileFields
        title="Betreffzeile 2 (Dokumenten-Nr.)"
        config={layout.betreffzeile2}
        onChange={(betreffzeile2) => patch({ betreffzeile2 })}
        showFormat
      />
      <LogoFirmaFields
        config={layout.logo_firma}
        onChange={(logo_firma) => patch({ logo_firma })}
      />
      <InfoblockFields
        config={layout.infoblock}
        marginTopMm={layout.erste_seite.margin_top_mm}
        onChange={(infoblock) => patch({ infoblock })}
      />
    </>
  );
}

export function GdQrSignaturPanel({
  config,
  onChange,
}: {
  config: GdQrSignatur;
  onChange: (next: GdQrSignatur) => void;
}) {
  function patch(partial: Partial<GdQrSignatur>) {
    onChange({ ...config, ...partial });
  }

  return (
    <>
      <p className="gd-layout-config__hint">
        Kleiner QR-Code als Seitenmarker (Scan-Import) und Signaturblock mit optionalem großem
        QR-Code zur digitalen Freigabe. Die Live-Vorschau zeigt einen Beispiel-QR.
      </p>

      <div className="gd-layout-config__field-group">
        <h3>Kleiner QR-Code (jede Seite)</h3>
        <p className="gd-layout-config__hint">
          Position vom physischen Blattrand: 0&nbsp;mm Oben = obere Blattkante, 0&nbsp;mm Rechts =
          rechte Blattkante.
        </p>
        <div className="mb-3">
          <Checkbox
            label="Kleinen QR-Code auf jeder Seite anzeigen"
            checked={config.mini_qr_anzeigen}
            onChange={(e) => patch({ mini_qr_anzeigen: e.target.checked })}
          />
        </div>
        {config.mini_qr_anzeigen ? (
          <div className="gd-layout-config__grid-3">
            <TextInput
              label="Oben (mm)"
              type="number"
              value={String(config.mini_qr_oben_mm)}
              onChange={(e) => patch({ mini_qr_oben_mm: num(e.target.value) })}
              hint="Vom oberen Blattrand"
            />
            <TextInput
              label="Rechts (mm)"
              type="number"
              value={String(config.mini_qr_rechts_mm)}
              onChange={(e) => patch({ mini_qr_rechts_mm: num(e.target.value) })}
              hint="Vom rechten Blattrand"
            />
            <TextInput
              label="Größe (mm)"
              type="number"
              value={String(config.mini_qr_groesse_mm)}
              onChange={(e) => patch({ mini_qr_groesse_mm: num(e.target.value) })}
            />
          </div>
        ) : null}
      </div>

      <div className="gd-layout-config__field-group">
        <h3>Unterschriften &amp; großer QR</h3>
        <p className="gd-layout-config__hint">
          Position vom physischen Blattrand auf der letzten Seite: 0&nbsp;mm Links = linke
          Blattkante, 0&nbsp;mm Unten = untere Blattkante. Welche Unterschriften nötig sind,
          kannst du einzeln festlegen. Bei digitalem QR-Code wird die Kundenunterschrift
          automatisch mit angezeigt.
        </p>
        <div className="flex flex-col gap-2 mb-3">
          <Checkbox
            label="Signaturbereich auf letzter Seite anzeigen"
            checked={config.signaturblock_anzeigen}
            onChange={(e) => patch({ signaturblock_anzeigen: e.target.checked })}
          />
        </div>
        {config.signaturblock_anzeigen ? (
          <>
            <div className="gd-layout-config__field-group">
              <h3>Benötigte Unterschriften</h3>
              <div className="flex flex-col gap-2 mb-3">
                <Checkbox
                  label="Mitarbeiterunterschrift"
                  checked={config.mitarbeiter_unterschrift_anzeigen}
                  onChange={(e) =>
                    patch({ mitarbeiter_unterschrift_anzeigen: e.target.checked })
                  }
                />
                <Checkbox
                  label="Kundenunterschrift"
                  checked={
                    config.kunden_unterschrift_anzeigen || config.grosser_qr_anzeigen
                  }
                  disabled={config.grosser_qr_anzeigen}
                  onChange={(e) =>
                    patch({ kunden_unterschrift_anzeigen: e.target.checked })
                  }
                />
                <Checkbox
                  label="Großen QR-Code zur digitalen Unterschrift anzeigen"
                  checked={config.grosser_qr_anzeigen}
                  onChange={(e) => {
                    const on = e.target.checked;
                    patch({
                      grosser_qr_anzeigen: on,
                      ...(on ? { kunden_unterschrift_anzeigen: true } : {}),
                    });
                  }}
                />
              </div>
              {config.mitarbeiter_unterschrift_anzeigen ? (
                <TextInput
                  label="Beschriftung Mitarbeiterunterschrift"
                  value={config.mitarbeiter_unterschrift_label}
                  onChange={(e) =>
                    patch({ mitarbeiter_unterschrift_label: e.target.value })
                  }
                />
              ) : null}
              {config.kunden_unterschrift_anzeigen || config.grosser_qr_anzeigen ? (
                <div className={config.mitarbeiter_unterschrift_anzeigen ? "mt-3" : undefined}>
                  <TextInput
                    label="Beschriftung Kundenunterschrift"
                    value={config.signatur_label}
                    onChange={(e) => patch({ signatur_label: e.target.value })}
                    hint="Platzhalter möglich, z. B. {{Kunde.name}}"
                  />
                  <div className="mt-2">
                    <SelectInput
                      label="Platzhalter einfügen"
                      value=""
                      onChange={(e) => {
                        const token = e.target.value;
                        if (!token) return;
                        const current = config.signatur_label || "";
                        const sep = current && !current.endsWith(" ") ? " " : "";
                        patch({ signatur_label: `${current}${sep}${token}` });
                      }}
                    >
                      <option value="">Auswählen…</option>
                      <option value="{{Kunde.name}}">Kundenname / Firma</option>
                      <option value="{{Kunde.stamm_name}}">Kundenstamm-Name</option>
                      <option value="{{Ansprechpartner.name}}">Ansprechpartner</option>
                      <option value="{{Ansprechpartner.anrede}}">z. Hd. …</option>
                      <option value="{{Projekt.name}}">Projektname</option>
                      <option value="{{Dokument.typ}}">Dokumenttyp</option>
                      <option value="{{Dokument.nummer}}">Dokument-Nr.</option>
                    </SelectInput>
                  </div>
                </div>
              ) : null}
              {config.grosser_qr_anzeigen ? (
                <p className="gd-layout-config__hint mt-2">
                  Kundenunterschrift ist aktiv, weil der digitale QR-Code eingeblendet ist.
                </p>
              ) : null}
            </div>

            <div className="gd-layout-config__grid-3 mt-3">
              <TextInput
                label="Links (mm)"
                type="number"
                value={String(config.signatur_links_mm)}
                onChange={(e) => patch({ signatur_links_mm: num(e.target.value) })}
                hint="Vom linken Blattrand"
              />
              <TextInput
                label="Unten (mm)"
                type="number"
                value={String(config.signatur_unten_mm)}
                onChange={(e) => patch({ signatur_unten_mm: num(e.target.value) })}
                hint="Vom unteren Blattrand"
              />
              <TextInput
                label="Breite (mm)"
                type="number"
                value={String(config.signatur_breite_mm)}
                onChange={(e) => patch({ signatur_breite_mm: num(e.target.value) })}
              />
            </div>
            {config.grosser_qr_anzeigen ? (
              <div className="mt-3">
                <TextareaInput
                  label="Hinweistext neben dem QR-Code"
                  value={config.grosser_qr_hinweis}
                  onChange={(e) => patch({ grosser_qr_hinweis: e.target.value })}
                  rows={3}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}

export function GdZusaetzlicheOptionenPanel({
  config,
  onChange,
}: {
  config: GdZusaetzlicheOptionen;
  onChange: (next: GdZusaetzlicheOptionen) => void;
}) {
  const pdfFilenameRef = useRef<HTMLInputElement | null>(null);
  const [categories, setCategories] = useState<GdPlaceholderCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState("projekt");

  useEffect(() => {
    let cancelled = false;
    void fetchGdPlaceholderCatalog().then((cats) => {
      if (cancelled) return;
      setCategories(cats);
      if (cats.length && !cats.some((c) => c.key === activeCategory)) {
        setActiveCategory(cats[0]!.key);
      }
    });
    return () => {
      cancelled = true;
    };
    // Nur beim Mount laden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patch(partial: Partial<GdZusaetzlicheOptionen>) {
    onChange({ ...config, ...partial });
  }

  function insertPdfFilenameToken(token: string) {
    const input = pdfFilenameRef.current;
    const current = config.pdf_dateiname || "";
    if (!input) {
      patch({ pdf_dateiname: `${current}${token}` });
      return;
    }
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    patch({ pdf_dateiname: next });
    requestAnimationFrame(() => {
      const pos = start + token.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  }

  return (
    <>
      <div className="gd-layout-config__field-group">
        <h3>PDF-Dateiname</h3>
        <p className="gd-layout-config__hint gd-layout-config__info-box">
          Gilt für Download, E-Mail-Anhang und Ablage im Projektordner. Platzhalter werden beim
          Erzeugen der PDF mit den Dokumentdaten ersetzt.
        </p>
        <TextInput
          ref={pdfFilenameRef}
          label="Dateiname-Vorlage"
          value={config.pdf_dateiname || DEFAULT_PDF_DATEINAME}
          onChange={(e) => patch({ pdf_dateiname: e.target.value })}
          placeholder={DEFAULT_PDF_DATEINAME}
          hint="Beispiel: {{Projekt.nummer}}-{{Dokument.typ}}-{{Dokument.nummer}}.pdf"
        />
        {categories.length ? (
          <div className="mt-3 h-56 overflow-hidden rounded-lg border border-separator">
            <GdPlaceholderPicker
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              onInsert={insertPdfFilenameToken}
            />
          </div>
        ) : null}
      </div>

      <div className="gd-layout-config__field-group">
        <h3>Betreffzeilen für Rechnungen</h3>
        <div className="gd-layout-config__grid-2">
          <TextInput
            label="Betreff Schlussrechnung"
            value={config.betreff_abschlussrechnung}
            onChange={(e) => patch({ betreff_abschlussrechnung: e.target.value })}
          />
          <TextInput
            label="Betreff Teilrechnung"
            value={config.betreff_teilrechnung}
            onChange={(e) => patch({ betreff_teilrechnung: e.target.value })}
          />
          <TextInput
            label="Betreff Stornorechnung"
            value={config.betreff_stornorechnung}
            onChange={(e) => patch({ betreff_stornorechnung: e.target.value })}
          />
          <TextInput
            label="Betreff Vorkasse-Rechnung"
            value={config.betreff_vorkasse}
            onChange={(e) => patch({ betreff_vorkasse: e.target.value })}
          />
        </div>
      </div>

      <div className="gd-layout-config__field-group">
        <h3>Rechnungsoptionen</h3>
        <div className="flex flex-col gap-2 mb-3">
          <Checkbox
            label="Rechnungsübersicht anzeigen"
            checked={config.rechnungsuebersicht_anzeigen}
            onChange={(e) => patch({ rechnungsuebersicht_anzeigen: e.target.checked })}
          />
          <Checkbox
            label="Lohn- und Maschinenkosten ausweisen"
            checked={config.lohn_maschinenkosten_ausweisen}
            onChange={(e) => patch({ lohn_maschinenkosten_ausweisen: e.target.checked })}
          />
        </div>
        {config.lohn_maschinenkosten_ausweisen ? (
          <TextareaInput
            label="Text Lohn- und Maschinenkosten"
            value={config.lohn_maschinenkosten_text}
            onChange={(e) => patch({ lohn_maschinenkosten_text: e.target.value })}
            rows={2}
            hint="Platzhalter: {summe}"
          />
        ) : null}
      </div>

      <div className="gd-layout-config__field-group">
        <h3>Zahlungsbedingungen</h3>
        <p className="gd-layout-config__hint gd-layout-config__info-box">
          Der Hinweis „Zahlbar innerhalb von … Tagen“ / „Vorkasse“ und die Bankdaten erscheinen
          nur auf Rechnungen – nicht auf Angebot, Lieferschein oder Auftragsbestätigung. Dieselben
          Standardwerte sind unter Kontakte → Kunden pflegbar. Wenn Sie die Bedingungen im
          Schlusstext pflegen, können Sie den automatischen Hinweis ausblenden.
        </p>
        <div className="mb-3">
          <Checkbox
            label="Zahlungsbedingungen-Hinweis auf Dokument anzeigen"
            checked={config.zahlungsbedingungen_anzeigen}
            onChange={(e) => patch({ zahlungsbedingungen_anzeigen: e.target.checked })}
          />
        </div>
        <div className="gd-layout-config__grid-3">
          <SelectInput
            label="Zahlungsart"
            value={config.zahlungsart || "rechnung"}
            onChange={(e) => {
              const zahlungsart = e.target.value;
              patch({
                zahlungsart,
                ...(zahlungsart === "vorkasse" ? { zahlungsziel_tage: 0 } : {}),
              });
            }}
          >
            {GD_ZAHLUNGSBEDINGUNG_ART_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectInput>
          <TextInput
            label="Zahlungsziel (Tage)"
            type="number"
            value={String(config.zahlungsziel_tage)}
            disabled={config.zahlungsart === "vorkasse"}
            onChange={(e) => patch({ zahlungsziel_tage: num(e.target.value) })}
          />
          <TextInput
            label="Skonto %"
            type="number"
            value={String(config.skonto_prozent)}
            onChange={(e) => patch({ skonto_prozent: num(e.target.value) })}
          />
          <TextInput
            label="Skontoziel (Tage)"
            type="number"
            value={String(config.skontoziel_tage)}
            onChange={(e) => patch({ skontoziel_tage: num(e.target.value) })}
          />
        </div>
      </div>

      <div className="gd-layout-config__field-group">
        <h3>Dokumentenspezifische Bankinformationen</h3>
        <p className="gd-layout-config__hint gd-layout-config__info-box">
          Diese Bankverbindung kann im Fußbereich dieses Dokumenttyps angezeigt werden.
          Die USt-IdNr. wird für ZUGFeRD- und X-Rechnungen benötigt.
        </p>
        <div className="mb-3">
          <Checkbox
            label="Bankinformationen auf Dokument anzeigen"
            checked={config.bank_anzeigen}
            onChange={(e) => patch({ bank_anzeigen: e.target.checked })}
          />
        </div>
        <div className="gd-layout-config__grid-2">
          <TextInput
            label="USt-IdNr. (Verkäufer)"
            value={config.verkaeufer_ust_id}
            onChange={(e) => patch({ verkaeufer_ust_id: e.target.value })}
            placeholder="DE123456789"
          />
          <TextInput
            label="Steuernummer (Fallback zur USt-IdNr.)"
            value={config.verkaeufer_steuernummer}
            onChange={(e) => patch({ verkaeufer_steuernummer: e.target.value })}
            placeholder="045/120/12345"
          />
        </div>
        <div className="mt-3">
          <TextInput
            label="Kontoinhaber"
            value={config.bank_kontoinhaber}
            onChange={(e) => patch({ bank_kontoinhaber: e.target.value })}
          />
        </div>
        <div className="mt-3">
          <TextInput
            label="Kreditinstitut"
            value={config.bank_kreditinstitut}
            onChange={(e) => patch({ bank_kreditinstitut: e.target.value })}
          />
        </div>
        <div className="gd-layout-config__grid-2 mt-3">
          <TextInput
            label="IBAN"
            value={config.bank_iban}
            onChange={(e) => patch({ bank_iban: e.target.value })}
          />
          <TextInput
            label="BIC"
            value={config.bank_bic}
            onChange={(e) => patch({ bank_bic: e.target.value })}
          />
        </div>
      </div>

      <div className="gd-layout-config__field-group">
        <h3>E-Rechnung: Verkäuferkontakt</h3>
        <p className="gd-layout-config__hint gd-layout-config__info-box">
          Die XRechnung verlangt einen Ansprechpartner des Rechnungsstellers mit Name,
          Telefonnummer und E-Mail-Adresse (BT-41/42/43). Bleiben die Felder leer, werden
          Name, Telefon und E-Mail der Person verwendet, die die Rechnung erstellt hat.
        </p>
        <TextInput
          label="Kontakt (Name/Abteilung)"
          value={config.verkaeufer_kontakt_name}
          onChange={(e) => patch({ verkaeufer_kontakt_name: e.target.value })}
          placeholder="z. B. Buchhaltung"
        />
        <div className="gd-layout-config__grid-2 mt-3">
          <TextInput
            label="Telefon"
            value={config.verkaeufer_kontakt_telefon}
            onChange={(e) => patch({ verkaeufer_kontakt_telefon: e.target.value })}
            placeholder="+49 …"
          />
          <TextInput
            label="E-Mail"
            value={config.verkaeufer_kontakt_email}
            onChange={(e) => patch({ verkaeufer_kontakt_email: e.target.value })}
            placeholder="rechnung@…"
          />
        </div>
      </div>
    </>
  );
}
